import React, { useEffect, useMemo, useState } from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Collapse,
  Grid,
  TextField,
  Typography
} from '@material-ui/core';
import Autocomplete from '@material-ui/lab/Autocomplete';
import { alpha, makeStyles, Theme, ThemeProvider } from '@material-ui/core/styles';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import { ScaleLoader } from 'react-spinners';
import { useSnackbar } from 'notistack';
import { BuildTheme } from '../../../../theme/Theme';
import { LanguageCode, RefConstants } from '../../../../helpers/Constants';
import { useAppSelector } from '../../../../store/configureStore';
import { IReferential } from '../../../../interfaces/IReferential';
import { IOrganizationService } from '../../../../services/Interfaces/IOrganizationService';
import { IReferentialService } from '../../../../services/Interfaces/IReferentialService';
import { ITranslatorService } from '../../../../services/Interfaces/ITranslatorService';
import {
  PartMappingDto,
  PartMappingFilterRequestDto,
  PartMappingTreeBodyTypeDto
} from '../../../../interfaces/Organization';
import PartMappingTreeDamageType from './PartMappingTreeDamageType';

interface IPartMappingTreeProps {
  organizationId: number;
  translatorService: ITranslatorService;
  organizationService: IOrganizationService;
  referentialService: IReferentialService;
}

interface IPartMappingTreeContentProps {
  bodyTypes: PartMappingTreeBodyTypeDto[];
  translatorService: ITranslatorService;
}

interface IPartMappingTreeBodyTypeProps {
  bodyType: PartMappingTreeBodyTypeDto;
  translatorService: ITranslatorService;
}

const useStyles = makeStyles((theme: Theme) => ({
  bodyTypeTitle: {
    fontWeight: theme.typography.fontWeightBold
  },
  details: {
    width: '100%'
  },
  sectionTitle: {
    display: 'block',
    fontWeight: theme.typography.fontWeightBold
  },
  scroll: {
    maxHeight: theme.spacing(70),
    overflowY: 'auto'
  },
  partList: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(0.75)
  },
  part: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    padding: theme.spacing(1.25, 1.5),
    borderRadius: theme.shape.borderRadius,
    border: `1px solid ${theme.palette.divider}`,
    background: theme.palette.background.paper,
    cursor: 'pointer'
  },
  partExpanded: {
    borderColor: theme.palette.primary.main,
    background: alpha(theme.palette.primary.main, 0.08)
  },
  partLabel: {
    flex: 1,
    fontWeight: theme.typography.fontWeightBold
  },
  expandIcon: {
    transition: theme.transitions.create('transform')
  },
  expandIconOpen: {
    transform: 'rotate(180deg)'
  },
  damageTypes: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1.25),
    padding: theme.spacing(1.25, 0, 0.5, 3)
  },
  empty: {
    padding: theme.spacing(2),
    textAlign: 'center',
    color: theme.palette.grey[500]
  }
}));

const PartMappingTreeBodyType: React.FC<IPartMappingTreeBodyTypeProps> = ({
  bodyType,
  translatorService
}) => {
  const classes = useStyles();
  const [expandedPartIds, setExpandedPartIds] = useState<number[]>([]);

  const togglePart = (partId: number) =>
    setExpandedPartIds((partIds) =>
      partIds.includes(partId) ? partIds.filter((item) => item !== partId) : [...partIds, partId]
    );

  const renderPart = (part: PartMappingDto) => {
    const isExpanded = expandedPartIds.includes(part.id);

    return (
      <div key={part.id}>
        <div
          className={`${classes.part} ${isExpanded ? classes.partExpanded : ''}`}
          onClick={() => togglePart(part.id)}
        >
          <Typography variant="body2" className={classes.partLabel}>
            {part.part?.displayName}
          </Typography>
          <ExpandMoreIcon
            className={`${classes.expandIcon} ${isExpanded ? classes.expandIconOpen : ''}`}
          />
        </div>
        <Collapse in={isExpanded} unmountOnExit>
          <div className={classes.damageTypes}>
            <Typography variant="overline" color="primary" className={classes.sectionTitle}>
              {translatorService.Tranlate('PART_MAPPING_TREE_DAMAGE_TYPES', 'Tipuri daună')}
            </Typography>
            {part.damageTypes.map((damageType) => (
              <PartMappingTreeDamageType
                key={damageType.id}
                damageType={damageType}
                translatorService={translatorService}
              />
            ))}
          </div>
        </Collapse>
      </div>
    );
  };

  return (
    <div className={`${classes.partList} ${classes.scroll}`}>
      <Typography variant="overline" color="primary" className={classes.sectionTitle}>
        {translatorService.Tranlate('PART_MAPPING_TREE_PARTS', 'Piese')}
      </Typography>
      {bodyType.parts.map((part) => renderPart(part))}
    </div>
  );
};

const PartMappingTreeContent: React.FC<IPartMappingTreeContentProps> = ({
  bodyTypes,
  translatorService
}) => {
  const classes = useStyles();

  if (bodyTypes.length === 0) {
    return (
      <Typography variant="body2" className={classes.empty}>
        {translatorService.Tranlate('PART_MAPPING_TREE_EMPTY', 'Nu există mapări de piese')}
      </Typography>
    );
  }

  return (
    <div>
      {bodyTypes.map((bodyType) => (
        <Accordion key={bodyType.bodyType.id} defaultExpanded={bodyTypes.length === 1}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant="subtitle1" className={classes.bodyTypeTitle}>
              {bodyType.bodyType.displayName}
            </Typography>
          </AccordionSummary>
          <AccordionDetails>
            <div className={classes.details}>
              <PartMappingTreeBodyType bodyType={bodyType} translatorService={translatorService} />
            </div>
          </AccordionDetails>
        </Accordion>
      ))}
    </div>
  );
};

const PartMappingTree: React.FC<IPartMappingTreeProps> = ({
  organizationId,
  translatorService,
  organizationService,
  referentialService
}) => {
  const theme = useMemo(() => BuildTheme(), []);
  const { enqueueSnackbar } = useSnackbar();
  const language = useAppSelector((state) => state.app.language);
  const [isLoading, setIsLoading] = useState(true);
  const [bodyTypeList, setBodyTypeList] = useState<IReferential[]>([]);
  const [selectedBodyTypes, setSelectedBodyTypes] = useState<IReferential[]>([]);
  const [bodyTypes, setBodyTypes] = useState<PartMappingTreeBodyTypeDto[]>([]);

  const showLoadError = (error: any) => {
    const message =
      error?.response?.status === 403
        ? translatorService.Tranlate(
            'PART_MAPPING_FORBIDDEN_MSG',
            'Nu aveți acces la maparea pieselor acestei organizații!'
          )
        : translatorService.Tranlate('ERROR_MSG', 'Eroare');
    enqueueSnackbar(message, { variant: 'error' });
  };

  const buildFilter = (): PartMappingFilterRequestDto =>
    ({
      organizationId: organizationId,
      bodyTypeIds: selectedBodyTypes.map((item: IReferential) => item.id),
      partIds: [],
      damageTypeIds: [],
      operationIds: [],
      damageSeverityIds: [],
      onlyParents: true,
      page: 1,
      pageSize: null,
      paginate: false,
      language: language == LanguageCode.RO ? LanguageCode.RO : LanguageCode.EN
    } as PartMappingFilterRequestDto);

  const loadBodyTypes = async () => {
    try {
      const response = await referentialService.Get(RefConstants.bodyTypeRef!.baseUrl);
      setBodyTypeList(response.filter((item: IReferential) => !item.isFleet));
    } catch (error) {
      showLoadError(error);
    }
  };

  const loadTree = async () => {
    setIsLoading(true);

    try {
      const response = await organizationService.GetPartMappingTree(buildFilter());
      setBodyTypes(response.bodyTypes);
    } catch (error) {
      showLoadError(error);
      setBodyTypes([]);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadBodyTypes();
  }, []);

  useEffect(() => {
    loadTree();
  }, [selectedBodyTypes]);

  return (
    <div className="m-3">
      <div className="mt-3">
        <Grid container spacing={2}>
          <Grid item xs={4}>
            <Box mb={2}>
              <Autocomplete
                id="bodyTypeFilter"
                multiple
                options={bodyTypeList}
                value={selectedBodyTypes}
                onChange={(e: any, newValue: IReferential[]) => setSelectedBodyTypes(newValue)}
                getOptionLabel={(option: IReferential) => option.displayName || ''}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    name="bodyTypeFilter"
                    label={translatorService.Tranlate('PART_MAPPING_BODY_TYPE_LABEL', 'Caroserie')}
                    fullWidth
                  />
                )}
              />
            </Box>
          </Grid>
        </Grid>
      </div>
      <div className="d-flex flex-row text-center flex-wrap justify-content-center">
        <ScaleLoader color={'var(--primary)'} loading={isLoading} />
      </div>
      {!isLoading ? (
        <ThemeProvider theme={theme}>
          <PartMappingTreeContent bodyTypes={bodyTypes} translatorService={translatorService} />
        </ThemeProvider>
      ) : null}
    </div>
  );
};

export default PartMappingTree;
